const KEY_IOS_VERSION = 'ios_version_app'
const KEY_ANDROID_VERSION = 'android_version_app'

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Returns the iOS version string stored in localStorage
 * @returns {String} The iOS version string
 */
const getiOSVersion = () => localStorage.getItem(KEY_IOS_VERSION)

/**
 * Stores the iOS version string in localStorage
 * @param {String} version The iOS version string
 */
const storeiOSVersion = version => {
  localStorage.setItem(KEY_IOS_VERSION, version)
}

/**
 * Returns the Android version string stored in localStorage
 * @returns {String} The Android version string
 */
const getAndroidVersion = () => localStorage.getItem(KEY_ANDROID_VERSION)

/**
 * Stores the Android version string in localStorage
 * @param {String} version The Android version string
 */
const storeAndroidVersion = version => {
  localStorage.setItem(KEY_ANDROID_VERSION, version)
}

/**
 * Returns whether (true) or not (false) the app is being rendered within an iOS device
 * @returns {boolean}
 */
const isiOSDevice = () => {
  let isiOS = false
  try {
    const userAgent = window?.navigator.userAgent.toLowerCase()
    isiOS = /iphone|ipad|ipod/.test(userAgent)
  } catch (error) {
    console.error('Error checking if iOS device', error)
  }
  return isiOS
}

/**
 * Returns whether (true) or not (false) the app is being rendered within an iOS WKWebView
 * @param {String} version The minimum version string to compare against (e.g., "1.0.0", "1.0.1", etc.)
 */
const isiOS = version => {
  const isWebkit = !!window?.webkit

  // If we didn't pass a version, just check if we're in a WKWebView
  if (!version) return isWebkit

  // Otherwise, check if we're in a WKWebView and if the version matches
  const iOSVersion = getiOSVersion()

  // If we don't have webkit or a version stored in localStorage, we're not in an iOS app
  if (!isWebkit || !iOSVersion) return false

  // Split the version string into an array of numbers
  // and compare the iOS version to the passed version
  const versionArray = version.split('.').map(Number)
  const iOSVersionArray = iOSVersion.split('.').map(Number)

  // Compare the version arrays and return true if the iOS version is greater than or equal to the passed version
  return iOSVersionArray.every(
    (value, index) =>
      value >= versionArray[index] || // Check if the current version is greater than or equal to the passed version
      (index > 0 && iOSVersionArray[index - 1] > versionArray[index - 1]) // Check if the previous version index is greater than the passed version
  )
}

/**
 * Returns whether (true) or not (false) the app is being rendered within an iOS AndroidWebwiew
 * @param {String} version The minimum version string to compare against (e.g., "1.0.0", "1.0.1", etc.)
 */
const isAndroid = version => {
  const isAndroidWebview = !!window?.Android

  // If we didn't pass a version, just check if we're in a AndroidWebview
  if (!version) return isAndroidWebview

  // Otherwise, check if we're in an AndroidWebview and if the version matches
  const AndroidVersion = getAndroidVersion()

  // If we don't have a webview or a version stored in localStorage, we're not in an Android app
  if (!isAndroidWebview || !AndroidVersion) return false

  // Split the version string into an array of numbers
  // and compare the Android version to the passed version
  const versionArray = version.split('.').map(Number)
  const androidVersionArray = AndroidVersion.split('.').map(Number)

  // Compare the version arrays and return true if the Android version is greater than or equal to the passed version
  return androidVersionArray.every(
    (value, index) =>
      value >= versionArray[index] || // Check if the current version is greater than or equal to the passed version
      (index > 0 && androidVersionArray[index - 1] > versionArray[index - 1]) // Check if the previous version index is greater than the passed version
  )
}

/**
 * Returns whether (true) or not (false) the app is being rendered within an iOS WKWebView or an Android webview
 */
const isInApp = () => isiOS() || isAndroid()

/**
 * Runs a callback loop on the mobile app to login using the privately stored credentials
 */
const privateAuth = () => {
  try {
    // NOTE: Webkit message handlers MUST be called with a value or they will not execute
    // See https://stackoverflow.com/questions/39388096/wkwebview-wkscriptmessagehandler-not-called-with-ios-10-xcode-8-beta
    if (isiOS()) window.webkit.messageHandlers.private_auth.postMessage('')
    if (isAndroid()) window.Android.privateAuth()
  } catch (error) {
    console.error(error)
  }
}

/**
 * Runs a callback on the mobile app to attempt OAuth login
 * @param {String} provider The provider to attempt OAuth login with (e.g., "apple", "google", etc.)
 */
const mobileOAuth = provider => {
  try {
    if (isiOS())
      window.webkit.messageHandlers.oauth.postMessage(
        JSON.stringify({
          provider
        })
      )
    if (isAndroid()) window.Android.oauth()
  } catch (error) {
    console.error(error)
  }
}

/**
 * Removes authentication info from the mobile application
 */
const logoutMobile = () => {
  try {
    // NOTE: Webkit message handlers MUST be called with a value or they will not execute
    // See https://stackoverflow.com/questions/39388096/wkwebview-wkscriptmessagehandler-not-called-with-ios-10-xcode-8-beta
    if (isiOS()) window.webkit.messageHandlers.logout.postMessage('')
    if (isAndroid()) window.Android.logOut()
  } catch (error) {
    console.error(error)
  }
}

/**
 * Posts authentication info to the mobile application
 */
const postMobileToken = token => {
  try {
    if (isiOS()) window.webkit.messageHandlers.token.postMessage(token)
    if (isAndroid()) window.Android.token(token)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Posts instagram sharing URL to mobile app
 */
const postMobileShareToInstagram = ({
  image,
  top_color = '#000',
  bottom_color = '#000'
}) => {
  try {
    if (isiOS()) {
      window.webkit.messageHandlers.instagram_stories.postMessage(
        JSON.stringify({
          post: {
            image,
            top_color,
            bottom_color
          }
        })
      )
    }
  } catch (error) {
    console.error(error)
  }
}

/**
 * Posts search info to the mobile application
 */
const postMobileSearch = searchResults => {
  try {
    if (isiOS())
      window.webkit.messageHandlers.search.postMessage(
        JSON.stringify(searchResults)
      )
  } catch (error) {
    console.error(error)
  }
}

/**
 * Save to Keychain
 */
const postMobileKeychain = auth => {
  try {
    if (isiOS())
      window.webkit.messageHandlers.auth.postMessage(JSON.stringify(auth))

    if (isAndroid()) window.Android.auth(auth.email, auth.password)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Toggles the mobile app's tab bar bar
 * @param {String} state The state to set the tab bar to, can be "on" or "off"
 */
const toggleTabBar = (state = 'on') => {
  try {
    if (isiOS()) window.webkit.messageHandlers.toggle_tabbar.postMessage(state)
    if (isAndroid()) window.Android.toggleBottomBar(state)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Open URL in Safari on iOS or Chrome on Android
 * @param {String} url The URL to open
 */
const openUrlInSafari = url => {
  try {
    if (isiOS()) window.webkit.messageHandlers.safari.postMessage(url)
    if (isAndroid()) window.Android.chrome(url)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Open URL in Default Browser
 * @param {String} url The URL to open
 */
const openUrlInAppBrowser = url => {
  try {
    if (isiOS()) window.webkit.messageHandlers.in_app_browser.postMessage(url)
    if (isAndroid()) window.Android.chrome(url)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Request notification autorization
 */
const requestNotificationAuthorization = () => {
  try {
    // NOTE: Webkit message handlers MUST be called with a value or they will not execute
    // See https://stackoverflow.com/questions/39388096/wkwebview-wkscriptmessagehandler-not-called-with-ios-10-xcode-8-beta
    if (isiOS())
      window.webkit.messageHandlers.notification_request.postMessage('')

    if (isAndroid()) window.Android.notificationRequest()
  } catch (error) {
    console.error(error)
  }
}

/**
 * Request In App Subscription Promo modal
 * @param {String} signupCde The promo code to apply to the subscription
 */
const requestInAppPromoModal = (signupCode = '') => {
  try {
    if (isiOS())
      window.webkit.messageHandlers.purchase_offers.postMessage(signupCode)

    if (isAndroid()) window.Android.purchaseOffers(signupCode)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Request In App Subscription Purchase
 * @param {Object} subscription The subscription to purchase
 * @param {Number} subscription.sku The SKU of the subscription
 * @param {Number} subscription.cost The cost of the subscription
 * @param {String} subscription.name The name of the subscription
 * @param {String} subscription.period The subscription period
 * @param {String} subscription.signup_code The promo code to apply to the subscription
 */
const requestInAppSubscriptionPurchase = subscription => {
  try {
    if (isiOS())
      window.webkit.messageHandlers.purchase_request.postMessage(
        JSON.stringify(subscription)
      )

    if (isAndroid())
      window.Android.purchaseRequest(JSON.stringify(subscription))
  } catch (error) {
    console.error(error)
  }
}

/**
 * Schedule a recurring notification with a given id, title, body and schedule
 * @param {Object} payload The notification payload to schedule. The payload must contain the following keys:
 * - id {String}: The id of the notification (e.g., "daily-remble", "affirmation", etc.)
 * - title {String}: The title of the notification
 * - body {String}: The body of the notification
 * - days {Array}: The days to schedule the notification for (0-6, 0 = Sunday)
 * - hour {Number}: The hour to schedule the notification for (0-23)
 * - minute {Number}: The minute to schedule the notification for (0-59)
 */
const scheduleNotificationsWithPayload = payload => {
  try {
    // See https://github.com/Remble/com.remble.ios/pull/26 for more info
    if (isiOS())
      window.webkit.messageHandlers.notification_schedule.postMessage(
        JSON.stringify(payload)
      )

    if (isAndroid())
      window.Android.notificationSchedule(JSON.stringify(payload))
  } catch (error) {
    console.error(error)
  }
}

/**
 * Stops scheduled notifications for a given notification schedule id
 * @param {String} id A stringified object with an `id` and array of `days` for the schedule to stop or "all" to stop all schedules
 * or a stringified object with a `key` value pair for the schedule to stop
 */
const stopScheduledNotifications = (id = 'all') => {
  try {
    // See https://github.com/Remble/com.remble.ios/pull/26 for more info
    // Cancel future notifications
    if (isiOS())
      window.webkit.messageHandlers.notification_cancel.postMessage(id)

    if (isAndroid()) window.Android.notificationCancel(id)
  } catch (error) {
    console.error(error)
  }
}

/**
 * Send haptic feedback to the mobile device
 * @param {String} type The type of haptic feedback to send.
 */
const sendHapticFeedback = (type = 'light') => {
  try {
    // NOTE: Webkit message handlers MUST be called with a value or they will not execute
    // See https://stackoverflow.com/questions/39388096/wkwebview-wkscriptmessagehandler-not-called-with-ios-10-xcode-8-beta
    if (isiOS()) window.webkit.messageHandlers.haptic.postMessage('')

    if (isAndroid()) window.Android.haptic()
  } catch (error) {
    console.error(error)
  }
}

export {
  isiOS,
  isiOSDevice,
  isAndroid,
  isInApp,
  getAndroidVersion,
  getiOSVersion,
  storeAndroidVersion,
  storeiOSVersion,
  logoutMobile,
  openUrlInSafari,
  openUrlInAppBrowser,
  postMobileToken,
  postMobileSearch,
  postMobileShareToInstagram,
  postMobileKeychain,
  privateAuth,
  mobileOAuth,
  toggleTabBar,
  requestNotificationAuthorization,
  requestInAppSubscriptionPurchase,
  requestInAppPromoModal,
  scheduleNotificationsWithPayload,
  stopScheduledNotifications,
  sendHapticFeedback
}
